import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { addUserAsProviderManager, getTeamManagers } from '../../../../services/staffingService';
import DashboardSidebar from "../agencyDashboardSidebar";
import { useProviderCompany } from '../../../../context/ProviderCompanyContext';
import {
  Users, Mail, Shield, Calendar, Plus, Trash2,
  Loader2, X, AlertCircle,
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import toast from 'react-hot-toast';

const ROLES = {
  admin:                     { label: 'Admin',               colors: 'bg-red-50 text-red-700 border-red-200'    },
  manager_managing_managers: { label: 'Sr. Manager',         colors: 'bg-violet-50 text-violet-700 border-violet-200' },
  dispatch_manager:          { label: 'Dispatch Manager',    colors: 'bg-blue-50 text-blue-700 border-blue-200' },
};

const Team = () => {
  const { providerCompanyId } = useParams();
  const { providerCompanyData, fetchProviderCompanyData } = useProviderCompany();

  const [showSidebar, setShowSidebar]           = useState(false);
  const [managers, setManagers]                 = useState([]);
  const [loading, setLoading]                   = useState(true);
  const [showInviteModal, setShowInviteModal]   = useState(false);
  const [inviteEmail, setInviteEmail]           = useState('');
  const [inviteRole, setInviteRole]             = useState('dispatch_manager');
  const [isInviting, setIsInviting]             = useState(false);
  const [showRemoveConfirm, setShowRemoveConfirm] = useState(null);

  const fetchTeam = async () => {
    try {
      const resp = await getTeamManagers(providerCompanyId);
      setManagers(resp.data.providerManagers || []);
    } catch (err) {
      console.error('Failed to fetch team:', err);
      toast.error('Failed to load team members');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (providerCompanyId) {
      fetchProviderCompanyData(providerCompanyId);
      fetchTeam();
    }
  }, [providerCompanyId]);

  const formatDate = (date) => {
    if (!date) return '—';
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric', month: 'short', day: 'numeric',
    });
  };

  const handleInvite = async (e) => {
    e.preventDefault();
    if (!inviteEmail.trim()) return;
    setIsInviting(true);
    try {
      await addUserAsProviderManager(providerCompanyId, inviteEmail.trim());
      toast.success(`Invitation sent to ${inviteEmail}`);
      setInviteEmail('');
      setInviteRole('dispatch_manager');
      setShowInviteModal(false);
      fetchTeam();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to send invitation');
    } finally {
      setIsInviting(false);
    }
  };

  const handleRemove = (index) => {
    const updated = managers.filter((_, i) => i !== index);
    setManagers(updated);
    setShowRemoveConfirm(null);
    toast.success('Member removed');
  };

  const companyName = providerCompanyData?.companyName || '';

  return (
    <div className="flex h-screen overflow-hidden bg-background font-inter">
      <DashboardSidebar isOpen={showSidebar} setIsOpen={setShowSidebar} />

      <main className="flex-1 overflow-y-auto">
        <div className="p-6">
          <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}>

            {/* Header */}
            <div className="mb-6 flex items-center justify-between">
              <div>
                <h2 className="text-2xl font-bold text-foreground">Team</h2>
                <p className="mt-1 text-sm text-muted-foreground">
                  {managers.length} member{managers.length !== 1 ? 's' : ''}
                  {companyName ? ` at ${companyName}` : ''}
                </p>
              </div>
              <button
                onClick={() => setShowInviteModal(true)}
                className="flex items-center gap-2 rounded-lg bg-brand-pink px-4 py-2.5 text-sm font-medium text-white transition-colors hover:bg-brand-pink/90"
              >
                <Plus size={16} />
                Invite Member
              </button>
            </div>

            {/* Table */}
            <div className="overflow-hidden rounded-xl border border-border bg-card shadow-sm">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-border bg-muted/50">
                      {['Member', 'Email', 'Role', 'Joined', 'Action'].map((h) => (
                        <th key={h} className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {loading ? (
                      Array.from({ length: 4 }).map((_, i) => (
                        <tr key={i}>
                          {Array.from({ length: 5 }).map((_, j) => (
                            <td key={j} className="px-4 py-3">
                              <div className="h-4 w-24 animate-pulse rounded bg-muted" />
                            </td>
                          ))}
                        </tr>
                      ))
                    ) : managers.length === 0 ? (
                      <tr>
                        <td colSpan={5} className="px-4 py-10 text-center text-sm text-muted-foreground">
                          No team members yet. Invite someone to get started.
                        </td>
                      </tr>
                    ) : (
                      managers.map((manager, idx) => {
                        if (!manager || !manager.user_id) return null;
                        const u = manager.user_id;
                        const fullName = u.firstName && u.lastName
                          ? `${u.firstName} ${u.lastName}`
                          : u.email || 'Unknown';
                        const initials = u.firstName
                          ? u.firstName.charAt(0).toUpperCase()
                          : (u.email?.charAt(0).toUpperCase() || 'M');
                        const roleKey   = manager.role?._id || manager.role || '';
                        const roleInfo  = ROLES[roleKey] || { label: roleKey || 'Member', colors: 'bg-muted text-muted-foreground border-border' };

                        return (
                          <motion.tr
                            key={u._id || idx}
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            transition={{ delay: idx * 0.04 }}
                            className="transition-colors hover:bg-muted/30"
                          >
                            {/* Member */}
                            <td className="px-4 py-3">
                              <div className="flex items-center gap-3">
                                <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center overflow-hidden rounded-full bg-primary/10">
                                  {u.profilePictureUrl ? (
                                    <img src={u.profilePictureUrl} alt={fullName} className="h-full w-full object-cover" />
                                  ) : (
                                    <span className="text-xs font-semibold text-primary">{initials}</span>
                                  )}
                                </div>
                                <p className="text-sm font-medium text-card-foreground">{fullName}</p>
                              </div>
                            </td>

                            {/* Email */}
                            <td className="px-4 py-3">
                              <div className="flex items-center gap-2 text-sm text-card-foreground">
                                <Mail size={13} className="text-muted-foreground" />
                                {u.email || '—'}
                              </div>
                            </td>

                            {/* Role */}
                            <td className="px-4 py-3">
                              <span className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-medium ${roleInfo.colors}`}>
                                <Shield size={11} />
                                {roleInfo.label}
                              </span>
                            </td>

                            {/* Joined */}
                            <td className="px-4 py-3">
                              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                                <Calendar size={13} />
                                {formatDate(manager.createdAt || u.createdAt)}
                              </div>
                            </td>

                            {/* Action */}
                            <td className="px-4 py-3">
                              {roleKey !== 'admin' ? (
                                <button
                                  onClick={() => setShowRemoveConfirm(idx)}
                                  className="flex items-center gap-1 text-xs text-red-500 transition-colors hover:text-red-700"
                                >
                                  <Trash2 size={13} />
                                  Remove
                                </button>
                              ) : (
                                <span className="text-xs text-muted-foreground">—</span>
                              )}
                            </td>
                          </motion.tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
            </div>

          </motion.div>
        </div>
      </main>

      {/* ===== Invite Modal ===== */}
      <AnimatePresence>
        {showInviteModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
            onClick={() => setShowInviteModal(false)}
          >
            <motion.div
              initial={{ scale: 0.95 }}
              animate={{ scale: 1 }}
              exit={{ scale: 0.95 }}
              className="w-full max-w-md rounded-xl border border-border bg-card p-6 shadow-xl"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="mb-4 flex items-center justify-between">
                <h3 className="text-lg font-bold text-foreground">Invite Team Member</h3>
                <button
                  onClick={() => setShowInviteModal(false)}
                  className="text-muted-foreground transition-colors hover:text-foreground"
                >
                  <X size={20} />
                </button>
              </div>

              <form onSubmit={handleInvite} className="space-y-4">
                <div>
                  <label className="mb-1 block text-sm font-medium text-foreground">Email Address</label>
                  <input
                    type="email"
                    value={inviteEmail}
                    onChange={(e) => setInviteEmail(e.target.value)}
                    placeholder="name@example.com"
                    className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-brand-pink"
                    disabled={isInviting}
                    autoFocus
                    required
                  />
                </div>

                <div>
                  <label className="mb-1 block text-sm font-medium text-foreground">Role</label>
                  <select
                    value={inviteRole}
                    onChange={(e) => setInviteRole(e.target.value)}
                    className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-brand-pink"
                    disabled={isInviting}
                  >
                    {Object.entries(ROLES).map(([val, { label }]) => (
                      <option key={val} value={val}>{label}</option>
                    ))}
                  </select>
                  <p className="mt-1 text-xs text-muted-foreground">
                    Admin: Full access · Sr. Manager: Manages managers · Dispatch Manager: Day-to-day operations
                  </p>
                </div>

                <div className="flex justify-end gap-2 pt-2">
                  <button
                    type="button"
                    onClick={() => setShowInviteModal(false)}
                    disabled={isInviting}
                    className="rounded-lg border border-border px-4 py-2 text-sm font-medium transition-colors hover:bg-muted disabled:opacity-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={isInviting}
                    className="flex items-center gap-2 rounded-lg bg-brand-pink px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-brand-pink/90 disabled:opacity-50"
                  >
                    {isInviting ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />}
                    Send Invite
                  </button>
                </div>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ===== Remove Confirmation Modal ===== */}
      <AnimatePresence>
        {showRemoveConfirm !== null && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
            onClick={() => setShowRemoveConfirm(null)}
          >
            <motion.div
              initial={{ scale: 0.95 }}
              animate={{ scale: 1 }}
              exit={{ scale: 0.95 }}
              className="w-full max-w-sm rounded-xl border border-border bg-card p-6 shadow-xl"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="mb-4 flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-full bg-red-50">
                  <AlertCircle size={20} className="text-red-500" />
                </div>
                <h3 className="text-lg font-bold text-foreground">Remove member?</h3>
              </div>
              <p className="mb-6 text-sm text-muted-foreground">
                Are you sure you want to remove this member from the team? This action cannot be undone.
              </p>
              <div className="flex justify-end gap-2">
                <button
                  onClick={() => setShowRemoveConfirm(null)}
                  className="rounded-lg border border-border px-4 py-2 text-sm font-medium transition-colors hover:bg-muted"
                >
                  Cancel
                </button>
                <button
                  onClick={() => handleRemove(showRemoveConfirm)}
                  className="rounded-lg bg-red-500 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-red-600"
                >
                  Remove
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default Team;
